import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  myInvitationsApi, sentInvitationsApi,
  acceptInvitationApi, declineInvitationApi, cancelInvitationApi,
} from "../api";
import AppPage from "../components/AppPage";

const STATUS_BADGE_CLS = {
  pending:   "bg-warning text-dark",
  accepted:  "bg-success",
  declined:  "bg-secondary",
  cancelled: "bg-secondary",
  expired:   "bg-secondary",
};

const fmtDate = (iso, locale) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Invitations({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const isAdmin = user && (user.role === "director" || user.role === "hr");
  const [tab, setTab] = useState("incoming");
  const [incoming, setIncoming] = useState([]);
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "ru-RU";

  const statusLabel = (s) => t(`invitations.status_${s}`, { defaultValue: s });

  const reload = async () => {
    setErr(""); setMsg("");
    try {
      const inc = await myInvitationsApi();
      setIncoming(inc);
      if (isAdmin) {
        const sn = await sentInvitationsApi();
        setSent(sn);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || t("common.load_error"));
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [isAdmin]);

  const onAccept = async (id, companyName) => {
    if (!window.confirm(t("invitations.accept_confirm", { company: companyName }))) return;
    setBusy(id); setErr(""); setMsg("");
    try {
      await acceptInvitationApi(id);
      setMsg(t("invitations.accepted_to", { company: companyName }));
      if (refreshUser) await refreshUser();
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(null); }
  };
  const onDecline = async (id) => {
    if (!window.confirm(t("invitations.decline_confirm"))) return;
    setBusy(id); setErr(""); setMsg("");
    try { await declineInvitationApi(id); setMsg(t("invitations.declined_msg")); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(null); }
  };
  const onCancel = async (id) => {
    if (!window.confirm(t("invitations.cancel_confirm"))) return;
    setBusy(id); setErr(""); setMsg("");
    try { await cancelInvitationApi(id); setMsg(t("invitations.cancelled_msg")); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(null); }
  };

  const Pending = (s) => s === "pending";

  const content = (
    <>
      {!embedded && (
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            {isAdmin ? (
              <Link to="/employees" className="small text-muted text-decoration-none d-inline-flex align-items-center mb-1"
                    data-testid="invitations-back-employees">
                <i className="bi bi-arrow-left me-1"></i>{t("nav.employees")}
              </Link>
            ) : (
              <Link to="/dashboard" className="small text-muted text-decoration-none d-inline-flex align-items-center mb-1"
                    data-testid="invitations-back-dashboard">
                <i className="bi bi-arrow-left me-1"></i>{t("nav.dashboard")}
              </Link>
            )}
            <h4 className="fw-bold mb-0"><i className="bi bi-envelope-paper me-2"></i>{t("invitations.title")}</h4>
          </div>
        </div>
      )}

      <ul className="nav nav-pills mb-3" data-testid="invitations-tabs">
        <li className="nav-item">
          <button className={`nav-link ${tab === "incoming" ? "active" : ""}`}
                  onClick={() => setTab("incoming")} data-testid="tab-incoming">
            {t("invitations.tab_incoming")}
            {incoming.filter(i => Pending(i.status)).length > 0 && (
              <span className="badge bg-danger ms-2">{incoming.filter(i => Pending(i.status)).length}</span>
            )}
          </button>
        </li>
        {isAdmin && (
          <li className="nav-item">
            <button className={`nav-link ${tab === "sent" ? "active" : ""}`}
                    onClick={() => setTab("sent")} data-testid="tab-sent">
              {t("invitations.tab_sent")}
            </button>
          </li>
        )}
      </ul>

      {err && <div className="alert alert-danger py-2" data-testid="inv-error">{err}</div>}
      {msg && <div className="alert alert-success py-2" data-testid="inv-success">{msg}</div>}

      {tab === "incoming" && (
        <div className="card card-soft" data-testid="incoming-list">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("invitations.th_company")}</th>
                  <th>{t("invitations.th_from")}</th>
                  <th>{t("invitations.th_position")}</th>
                  <th>{t("invitations.th_role")}</th>
                  <th className="text-end">{t("invitations.th_salary")}</th>
                  <th>{t("invitations.th_message")}</th>
                  <th>{t("invitations.th_date")}</th>
                  <th>{t("invitations.th_status")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {incoming.map(i => {
                  const cls = STATUS_BADGE_CLS[i.status] || "bg-secondary";
                  return (
                    <tr key={i.invite_id} data-testid={`inv-row-${i.invite_id}`}>
                      <td className="fw-semibold">{i.from_company_name || "—"}</td>
                      <td className="small">{i.from_user_name || "—"}</td>
                      <td className="small">{i.position || "—"}</td>
                      <td>{t(`roles.${i.role}`, { defaultValue: i.role })}</td>
                      <td className="text-end">{Math.round(i.salary || 0).toLocaleString(dateLocale)}</td>
                      <td className="small text-muted">{i.message ? `«${i.message}»` : "—"}</td>
                      <td className="small">{fmtDate(i.created_at, dateLocale)}</td>
                      <td><span className={`badge ${cls}`}>{statusLabel(i.status)}</span></td>
                      <td className="text-end">
                        {Pending(i.status) && (
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-success" disabled={busy === i.invite_id}
                                    onClick={() => onAccept(i.invite_id, i.from_company_name)}
                                    data-testid={`accept-${i.invite_id}`}>
                              <i className="bi bi-check-lg me-1"></i>{t("invitations.accept")}
                            </button>
                            <button className="btn btn-outline-danger" disabled={busy === i.invite_id}
                                    onClick={() => onDecline(i.invite_id)}
                                    data-testid={`decline-${i.invite_id}`}>
                              <i className="bi bi-x-lg me-1"></i>{t("invitations.decline")}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {incoming.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-muted py-4">{t("invitations.empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "sent" && isAdmin && (
        <div className="card card-soft" data-testid="sent-list">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("invitations.th_email")}</th>
                  <th>{t("invitations.th_name")}</th>
                  <th>{t("invitations.th_position")}</th>
                  <th>{t("invitations.th_role")}</th>
                  <th className="text-end">{t("invitations.th_salary")}</th>
                  <th>{t("invitations.th_date")}</th>
                  <th>{t("invitations.th_status")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sent.map(i => {
                  const cls = STATUS_BADGE_CLS[i.status] || "bg-secondary";
                  return (
                    <tr key={i.invite_id} data-testid={`sent-row-${i.invite_id}`}>
                      <td className="small">{i.to_email}</td>
                      <td>{i.to_user_name || "—"}</td>
                      <td className="small">{i.position || "—"}</td>
                      <td>{t(`roles.${i.role}`, { defaultValue: i.role })}</td>
                      <td className="text-end">{Math.round(i.salary || 0).toLocaleString(dateLocale)}</td>
                      <td className="small">{fmtDate(i.created_at, dateLocale)}</td>
                      <td><span className={`badge ${cls}`}>{statusLabel(i.status)}</span></td>
                      <td className="text-end">
                        {Pending(i.status) && (
                          <button className="btn btn-sm btn-outline-secondary" disabled={busy === i.invite_id}
                                  onClick={() => onCancel(i.invite_id)}
                                  data-testid={`cancel-${i.invite_id}`}>
                            <i className="bi bi-x me-1"></i>{t("invitations.cancel")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sent.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">{t("invitations.empty_sent")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <AppPage testId="invitations-page" bodyScroll="scroll">
      {content}
    </AppPage>
  );
}
